import { useMemo } from 'react';
import { isCarrierProjectType } from '../../../app/navigation/courier-project-type';
import { useQueryGetProject } from '../../../core/queries/projects.queries';
import { useProjectLabels, useUserLabels } from '../../../hooks/use-entity-labels.hook';
import {
  carrierAccessLevelDetailStrings,
  teamMembershipAccessLevelDetailStrings
} from '../details/logic/team-member-details.constants';

/**
 * The access level descriptions as this project would word them. `{project}` and `{users}` come from
 * the project's own labels, and a sentence that is wrong beyond its nouns is replaced outright.
 */
export function useAccessLevelDescriptions(projectID?: string): Record<string, string> {
  const project = useQueryGetProject(projectID);
  const projectLabels = useProjectLabels(projectID);
  const userLabels = useUserLabels(projectID);

  const projectType = project.data?.projectType;
  // The labels are title case for headings and menus; these read as prose.
  const projectNoun = projectLabels.singular.toLowerCase();
  const usersNoun = userLabels.plural.toLowerCase();

  return useMemo(() => {
    const overrides = isCarrierProjectType(projectType) ? carrierAccessLevelDetailStrings : {};

    return Object.fromEntries(
      Object.entries(teamMembershipAccessLevelDetailStrings).map(([level, description]) => [
        level,
        overrides[level] ?? fillNouns(description, projectNoun, usersNoun)
      ])
    );
  }, [projectType, projectNoun, usersNoun]);
}

// `replaceAll` is past this project's TS lib target, so the global regex does the same job.
function fillNouns(description: string, projectNoun: string, usersNoun: string): string {
  return description.replace(/\{project\}/g, projectNoun).replace(/\{users\}/g, usersNoun);
}
